import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/** Hook: returns { page, pageSize, setPage, setPageSize, totalPages, slice, range } */
export function usePagination(rows, defaultPageSize = 25) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    // reset to page 1 when rows length changes or page size changes
    useEffect(() => {
        setPage(1);
    }, [rows.length, pageSize]);

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    const end = Math.min(start + pageSize, rows.length);
    const slice = useMemo(() => rows.slice(start, end), [rows, start, end]);

    return {
        page: safePage,
        pageSize,
        setPage,
        setPageSize,
        totalPages,
        slice,
        range: { start: rows.length === 0 ? 0 : start + 1, end, total: rows.length },
    };
}

export default function Pagination({ page, totalPages, pageSize, range, setPage, setPageSize, sizes = [25, 50, 100, 200], dataTestId = "pagination" }) {
    // Compute visible page numbers — show first, last, current ± 1, and ellipses
    const visible = useMemo(() => {
        const out = new Set([1, totalPages, page, page - 1, page + 1]);
        return Array.from(out)
            .filter((p) => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b);
    }, [page, totalPages]);

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t border-zinc-200 bg-zinc-50 text-xs" data-testid={dataTestId}>
            <div className="flex items-center gap-3 text-zinc-500">
                <span className="tabular-nums">
                    Showing <strong className="text-zinc-700">{range.start}</strong>–
                    <strong className="text-zinc-700">{range.end}</strong> of{" "}
                    <strong className="text-zinc-700">{range.total}</strong>
                </span>
                <span className="text-zinc-400">·</span>
                <label className="inline-flex items-center gap-1.5">
                    <span>Per page</span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                        className="px-2 py-1 bg-white border border-zinc-300 rounded text-xs"
                        data-testid={`${dataTestId}-size-select`}
                    >
                        {sizes.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="flex items-center gap-1">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage(1)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-zinc-300 hover:bg-white disabled:opacity-40"
                    title="First page"
                    data-testid={`${dataTestId}-first`}
                >
                    <ChevronsLeft className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-zinc-300 hover:bg-white disabled:opacity-40"
                    title="Previous"
                    data-testid={`${dataTestId}-prev`}
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                {visible.map((p, i) => {
                    const prev = visible[i - 1];
                    const gap = prev && p - prev > 1;
                    return (
                        <span key={p} className="inline-flex items-center">
                            {gap && <span className="px-1 text-zinc-400">…</span>}
                            <button
                                type="button"
                                onClick={() => setPage(p)}
                                aria-current={p === page ? "page" : undefined}
                                className={`min-w-7 h-7 px-2 inline-flex items-center justify-center rounded border tabular-nums ${
                                    p === page
                                        ? "wl-accent-border wl-accent-text font-semibold"
                                        : "border-zinc-300 hover:bg-white text-zinc-700"
                                }`}
                                data-testid={`${dataTestId}-page-${p}`}
                            >
                                {p}
                            </button>
                        </span>
                    );
                })}

                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-zinc-300 hover:bg-white disabled:opacity-40"
                    title="Next"
                    data-testid={`${dataTestId}-next`}
                >
                    <ChevronRight className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-zinc-300 hover:bg-white disabled:opacity-40"
                    title="Last page"
                    data-testid={`${dataTestId}-last`}
                >
                    <ChevronsRight className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}
